import { createSelector } from 'reselect'
import { CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'
import config from 'constants/config'
import { isBookingProtectionAvailableForAccommodationOffer } from 'lib/checkout/bookingProtection/utils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'
import getIsInsurnaceDomesticTrip from '../view/getIsInsurnaceDomesticTrip'
import { isFlightItem, isBookingProtectionItem, isInsuranceBookingProtectionItem, isExperienceItem, isBNBLLEHotelItem, isLuxPlusSubscriptionItem, isSubscriptionJoinItem, isInsuranceItem } from 'lib/checkout/checkoutUtils'

export const isInsuranceBookingProtectionAvailableForCartItemTypes = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => isStandaloneExperience(state),
  (items, offers, isGift, isStandaloneExperience) => {
    if (isGift) {
      return false
    }

    const isInsurancePostPurchase = items.every(item => isInsuranceBookingProtectionItem(item) || isInsuranceItem(item))
    if (isInsurancePostPurchase) return true

    const filteredItems = items.filter(item =>
      !isLuxPlusSubscriptionItem(item) &&
      !isSubscriptionJoinItem(item) &&
      !isInsuranceBookingProtectionItem(item) &&
      !isInsuranceItem(item),
    )

    for (const item of filteredItems) {
      if (!isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_BOOKING_PROTECTION_ENABLED_LIST, item.itemType)) {
        return false
      }

      if (item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL) {
        const offer = offers[item.offerId]
        if (offer && !isBookingProtectionAvailableForAccommodationOffer(offer)) {
          return false
        }
      }

      if (isExperienceItem(item) && item.isBuyNowBookLater) {
        return false
      }

      if (isBNBLLEHotelItem(item)) {
        return false
      }
    }

    if (!filteredItems.length) return false

    if (isStandaloneExperience) {
      const experienceItemType:App.Checkout.ExperienceItemType = 'experience'
      if (!isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_BOOKING_PROTECTION_ENABLED_LIST, experienceItemType)) return false
    }

    return true
  },
)

const cartOnlyHasFlightsOrBookingProtection = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean =>
    items.length > 0 &&
  items.every(item => isFlightItem(item) || isBookingProtectionItem(item) || isInsuranceBookingProtectionItem(item)),
)

const cartOnlyHasExperiencesOrBookingProtection = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.every(item => isExperienceItem(item) || isBookingProtectionItem(item) || isInsuranceBookingProtectionItem(item)),
)

export const isInsuranceBookingProtectionAvailableForRegions = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getIsInsurnaceDomesticTrip(state),
  (state: App.State) => cartOnlyHasFlightsOrBookingProtection(state),
  (state: App.State) => cartOnlyHasExperiencesOrBookingProtection(state),
  (currentRegionCode, isDomesticTrip, cartOnlyHasFlightsOrBookingProtection, cartOnlyHasExperiencesOrBookingProtection) => {
    const isEnabled = isEnabledForFeature(config.INSURANCE_BOOKING_PROTECTION_ENABLED_REGIONS, currentRegionCode) && config.INSURANCE_BOOKING_PROTECTION_ENABLED
    if (!isEnabled) return false

    const isSGAndInternationalTrip = currentRegionCode === 'SG' && !isDomesticTrip
    if (isSGAndInternationalTrip && !cartOnlyHasFlightsOrBookingProtection && !cartOnlyHasExperiencesOrBookingProtection) return false

    return true
  },
)
